import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef, type ReactNode } from 'react';

const DEFAULT_ROW_HEIGHT = 52;
const DEFAULT_MAX_HEIGHT = '28rem';

type VirtualizedTableProps<T> = {
  rows: T[];
  /** Return a full `<tr>` element (including key). */
  renderRow: (row: T, index: number) => ReactNode;
  header: ReactNode;
  /** Rendered after the virtual padding, inside the same table body. */
  footer?: ReactNode;
  estimateRowHeight?: number;
  maxHeight?: string;
  tableClassName?: string;
  tbodyClassName?: string;
  wrapperClassName?: string;
  scrollClassName?: string;
};

/**
 * Table with sticky header and virtualized body rows inside a scroll container.
 */
export function VirtualizedTable<T>({
  rows,
  renderRow,
  header,
  footer = null,
  estimateRowHeight = DEFAULT_ROW_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  tableClassName = 'min-w-full text-body-sm',
  tbodyClassName = 'divide-y divide-border',
  wrapperClassName = 'overflow-hidden rounded-xl border border-border bg-surface shadow-sm',
  scrollClassName = 'overflow-auto custom-scrollbar',
}: VirtualizedTableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    overscan: 8,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0]!.start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1]!.end
      : 0;

  return (
    <div className={wrapperClassName}>
      <div ref={scrollRef} className={scrollClassName} style={{ maxHeight }}>
        <table className={tableClassName}>
          {header}
          <tbody className={tbodyClassName}>
            {paddingTop > 0 ? (
              <tr aria-hidden>
                <td style={{ height: paddingTop, padding: 0, border: 0 }} colSpan={100} />
              </tr>
            ) : null}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]!;
              return renderRow(row, virtualRow.index);
            })}
            {paddingBottom > 0 ? (
              <tr aria-hidden>
                <td style={{ height: paddingBottom, padding: 0, border: 0 }} colSpan={100} />
              </tr>
            ) : null}
            {footer}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @deprecated Use VirtualizedTable instead. */
export const VirtualizedTableBody = VirtualizedTable;
export const VirtualizedScrollTable = VirtualizedTable;
